import { BookingFormField } from '@domain/booking-form-field';
import { Price } from '@domain/price';
import { TicketHolderFormField } from '@domain/ticket-holder-form-field';

import { Addon } from './addon';
import { SeatingItem } from './floorplan';

export const ADMISSION_STATUS_ON_SALE = 'on_sale';
export const ADMISSION_STATUS_SALES_CLOSED = 'sales_closed';
export const ADMISSION_STATUS_SOLD_OUT = 'sold_out';
export const ADMISSION_STATUS_HIDDEN = 'hidden';
export const ADMISSION_STATUS_HIDDEN_BY_TRIGGER = 'hidden_by_trigger';
export const ADMISSION_STATUS_IN_REVIEW = 'in_review';

export type Status =
  | typeof ADMISSION_STATUS_HIDDEN
  | typeof ADMISSION_STATUS_ON_SALE
  | typeof ADMISSION_STATUS_SALES_CLOSED
  | typeof ADMISSION_STATUS_SOLD_OUT
  | typeof ADMISSION_STATUS_HIDDEN_BY_TRIGGER
  | typeof ADMISSION_STATUS_IN_REVIEW;

export const BOOKING_FORM_FIELDS = 'bookingFormFields';
export const TICKET_HOLDER_FORM_FIELDS = 'ticketHolderFormFields';

export interface SalesTime {
  openingTime: number | null;
  closingTime: number;
}

export interface SalesStatus {
  isSalesClosed: boolean;
  isSoldOut: boolean;
}

export interface Settings {
  channelDefaultMaxQuantity: number | null;
  isForAllChannels: boolean;
  isHidden: boolean;
  isPrivate: boolean;
  isNominative: boolean;
  italyRegulation?: {
    dirittoPrevendita: number;
    isSiaeApproved: boolean;
    isSiaeExcluded: boolean;
    needsCaptcha: boolean;
    needsUserVerified: boolean;
    allows18App: boolean;
    abbonamentoId: string | null;
    abbonamentoType: number | null;
  };
}

export interface Commissions {
  transactionFee: number;
  transactionFeeStrategy: string;
  xceedCommission: number;
  xceedCommissionStrategy: number;
}

export interface Translations {
  [key: string]: string | null;
  ca: string | null;
  de: string | null;
  en: string | null;
  es: string | null;
  fr: string | null;
  it: string | null;
  pt: string | null;
}

interface Event {
  id: string;
}

interface ItemsPerTransaction {
  min: number;
  max: number;
}

export interface CheckinTime {
  validFrom: number | null;
  validUntil: number | null;
}

export interface BaseAdmission {
  id: string;
  event: Event;
  price: Price;
  quantity: number | null;
  soldItems: number;
  availableItems: number | null;
  itemsPerTransaction: ItemsPerTransaction;
  salesTime: SalesTime;
  salesStatus: SalesStatus;
  settings: Settings;
  commissions: Commissions;
  displayName: string;
  displayDescription: string;
  name: Translations;
  description: Translations;
  bookingFormFields: BookingFormField[];
  ticketHolderFormFields: TicketHolderFormField[];
  floorplanId: string | null;
  accessCode: string | null;
  createdAt?: number; // Added while developing FloorplanV2 as optional to support backward compatibility
  updatedAt: number;
  color: string | null;
  addOns: Addon[];
  status: Status;
  isDraft: boolean;
  drinksIncluded?: number;
  checkInTime?: CheckinTime;
  admissionType: AdmissionType | typeof ADMISSION_TYPE_INVITATION;
  offerReleasedBefore?: string | null;
  hasMandatoryAddOns: boolean;
  addOnChoiceMode: string;
  channels?: Channel[];
  vatRate?: number | null;
  seatingItems?: Pick<SeatingItem, 'id' | 'label'>[];
}

export interface BottleService extends BaseAdmission {
  onlinePaymentPercentage: number;
  guestCapacity: number;
}

export interface Ticket extends BaseAdmission {
  onlinePaymentPercentage: number;
}

export interface GuestList extends BaseAdmission {
  // TODO: add typing
  // eslint-disable-next-line
  images: any[];
}

export type Admission = BottleService | GuestList | Ticket;

export const ADMISSION_TYPE_BOTTLE_SERVICE = 'bottleService';
export const ADMISSION_TYPE_GUEST_LIST = 'guestList';
export const ADMISSION_TYPE_TICKET = 'ticket';
export const ADMISSION_TYPE_SEASON_PASS = 'seasonPass';
export const ADMISSION_TYPE_INVITATION = 'invitation';
export const ADMISSION_TYPE_GROUP = 'group';

export type AdmissionType =
  | typeof ADMISSION_TYPE_BOTTLE_SERVICE
  | typeof ADMISSION_TYPE_GUEST_LIST
  | typeof ADMISSION_TYPE_TICKET;

interface Channel {
  id: string;
  maxQuantity: string | number | null;
}

export interface AdmissionWithChannels extends BaseAdmission {
  onlinePaymentPercentage?: number;
  guestCapacity?: number;
  // eslint-disable-next-line
  images?: any[];
  channels?: Channel[];
  isForAllChannels?: boolean;
  type: AdmissionType;
}
